import { useContext, useMemo } from 'react'
import { useAgenciesList } from '~/api/queries/agency'
import { useUsers } from '~/api/queries/user'
import { CompanyChipValue } from '~/components/Filters/FilterBar/chips/company'
import { OfferChipValue } from '~/components/Filters/FilterBar/chips/offer'
import { UsersChipValue } from '~/components/Filters/FilterBar/chips/users'
import type { FilterDefinition } from '~/components/Filters/FilterBar/types'
import { CompaniesAutocomplete } from '~/domains/companies/components/CompaniesAutocomplete'
import { OffersAutocomplete } from '~/domains/offers/components/OffersAutocomplete'
import { UsersAutocomplete } from '~/domains/users/components/UsersAutocomplete'
import { AuthContext } from '~/utils/contextes'

export const useApplicationFilterDefinitions = (): FilterDefinition[] => {
  const currentUser = useContext(AuthContext)
  const { data: agencies } = useAgenciesList('offers')
  const { data: users } = useUsers()
  const offerUserIds = useMemo(
    () =>
      users?.map((user) => user.id) ?? (currentUser ? [currentUser.id] : []),
    [users, currentUser]
  )

  return useMemo(
    () => [
      {
        key: 'status',
        label: 'Statut',
        type: 'select',
        multiSelect: true,
        options: [
          { value: 'untreated', label: 'Non traitées' },
          { value: 'treated', label: 'Traitées' },
          { value: 'archived', label: 'Refusées / Archivées' },
          { value: 'spam', label: 'Indésirables' }
        ],
        emptyLabel: 'Tous'
      },
      {
        key: 'spontaneous',
        label: 'Spontanées',
        type: 'select',
        options: [
          { value: 'only', label: 'Uniquement' },
          { value: 'exclude', label: 'Exclues' }
        ],
        emptyLabel: 'Incluses'
      },
      {
        key: 'archivedOffer',
        label: 'Offres archivées',
        type: 'select',
        options: [
          { value: 'only', label: 'Uniquement' },
          { value: 'exclude', label: 'Exclues' }
        ],
        emptyLabel: 'Incluses'
      },
      {
        key: 'offer',
        label: 'Offre',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <OffersAutocomplete
            key={value || 'empty'}
            userIds={offerUserIds}
            inlineList
            ids={value ? [value] : []}
            small
            fullWidth
            placeholder="Rechercher une offre"
            emptyLabel="Toutes"
            onChange={(v) => {
              onChange(v)
              onClose()
            }}
          />
        ),
        renderValue: (value) => <OfferChipValue id={value} />
      },
      {
        key: 'company',
        label: 'Entreprise',
        type: 'autocomplete',
        renderEditor: ({ value, onChange, onClose }) => (
          <CompaniesAutocomplete
            key={value || 'empty'}
            inlineList
            ids={value ? [value] : []}
            small
            fullWidth
            placeholder="Rechercher une entreprise"
            emptyLabel="Toutes"
            onChange={(v) => {
              onChange(v)
              onClose()
            }}
          />
        ),
        renderValue: (value) => <CompanyChipValue id={value} />
      },
      {
        key: 'users',
        label: 'Utilisateurs',
        type: 'autocomplete',
        renderEditor: ({ value, onChange }) => (
          <UsersAutocomplete
            key={value || 'empty'}
            inlineList
            multiple
            ids={
              value
                ? value
                    .split(',')
                    .map((entry) => entry.trim())
                    .filter(Boolean)
                : []
            }
            listingContext="offers"
            small
            fullWidth
            placeholder="Rechercher des utilisateurs"
            emptyLabel="Tous"
            onChange={(nextValues) => {
              if (nextValues.includes('')) {
                onChange('')
                return
              }
              onChange(nextValues.filter(Boolean).join(','))
            }}
          />
        ),
        renderValue: (value) => <UsersChipValue value={value} />
      },
      {
        key: 'agency',
        label: 'Agences',
        type: 'select',
        multiSelect: true,
        searchable: true,
        options:
          agencies?.map((agency) => ({
            label: agency.name,
            value: String(agency.id)
          })) ?? [],
        emptyLabel: 'Toutes'
      },
      {
        label: 'Reçues',
        menuLabel: 'Date de réception',
        type: 'dateRange',
        keyAfter: 'receivedAfter',
        keyBefore: 'receivedBefore'
      }
    ],
    [agencies, offerUserIds]
  )
}
